import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResumeUploadSection } from "./ResumeUploadSection";
import { Briefcase, Upload, ArrowRight } from "lucide-react";
import { formatJobType } from "@/utils/formatters";

interface JobPosition {
  id: string;
  role_name: string;
  description?: string;
  job_type: string;
  package_offered?: string;
}

type ResumeParseStatus = "idle" | "parsing" | "success" | "error";

interface PositionSelectionStepProps {
  jobPositions: JobPosition[];
  isLoadingJobPositions: boolean;
  selectedPosition: string;
  onPositionSelect: (position: string) => void;
  resumeFile: File | null;
  parseStatus: ResumeParseStatus;
  isVerifyingCaptcha: boolean;
  recaptchaLoaded: boolean;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRetry: () => void;
  onContinue: () => void;
  canContinue: boolean;
}

export function PositionSelectionStep({
  jobPositions,
  isLoadingJobPositions,
  selectedPosition,
  onPositionSelect,
  resumeFile,
  parseStatus,
  isVerifyingCaptcha,
  recaptchaLoaded,
  onFileChange,
  onRetry,
  onContinue,
  canContinue,
}: PositionSelectionStepProps) {
  const canProceed = selectedPosition && canContinue;
  const isProcessing = parseStatus === "parsing" || isVerifyingCaptcha;

  return (
    <div className="space-y-6">
      {/* Position Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Select Position
          </CardTitle>
          <CardDescription>
            Choose the position you're applying for. This will help us evaluate
            your resume more accurately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedPosition}
            onValueChange={onPositionSelect}
            disabled={isLoadingJobPositions}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoadingJobPositions
                    ? "Loading positions..."
                    : "Select a position"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {jobPositions.map((position) => (
                <SelectItem key={position.id} value={position.role_name}>
                  <div className="flex flex-col">
                    <span className="font-medium">{position.role_name}</span>
                    {position.job_type && (
                      <span className="text-sm text-gray-500">
                        {formatJobType(position.job_type)}
                        {position.package_offered &&
                          ` • ${position.package_offered}`}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Resume Upload - Only show if position is selected */}
      {selectedPosition && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Resume (Required)
            </CardTitle>
            <CardDescription>
              Upload your resume to continue with the registration form. Only
              PDF files are accepted (max 5MB).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResumeUploadSection
              resumeFile={resumeFile}
              parseStatus={parseStatus}
              isVerifyingCaptcha={isVerifyingCaptcha}
              recaptchaLoaded={recaptchaLoaded}
              onFileChange={onFileChange}
              onRetry={onRetry}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {selectedPosition && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-3">
              {parseStatus === "success" && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">
                    ✅ Resume processed successfully!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Your information has been extracted and the form will be
                    pre-filled.
                  </p>
                </div>
              )}

              {parseStatus === "error" && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-orange-800 font-medium">
                    ⚠️ Resume processing failed
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    Retry uploading your resume or proceed to the form after
                    verifying your details.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                {parseStatus === "error" && (
                  <Button
                    variant="outline"
                    onClick={onRetry}
                    disabled={isProcessing}
                  >
                    Retry Resume Upload
                  </Button>
                )}

                <Button
                  onClick={onContinue}
                  disabled={!canProceed || isProcessing}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  Proceed to Application Form
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>

              {isProcessing && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 font-medium">
                    {isVerifyingCaptcha
                      ? "🔒 Verifying security..."
                      : "⏳ Processing resume..."}
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    Please wait while we process your resume.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedPosition && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-gray-500">
              <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Select a position to continue</p>
              <p className="text-sm">
                Choose the role you're applying for to proceed with
                registration.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
